import React from 'react';
import { useTranslation } from 'react-i18next';
import './TableWidget.css';

// Maps backend KPI labels (English, from analyticsQueryCosmos.js) to i18n keys
const KPI_LABEL_TO_I18N = {
    'Total Calls': 'KPI_TOTAL_CALLS',
    'Serviced Calls': 'KPI_SERVICED_CALLS',
    'Abandon Calls': 'KPI_ABANDON_CALLS',
    'Redirected Calls': 'KPI_REDIRECTED_CALLS',
    'Service Level (< 30s)': 'KPI_SERVICE_LEVEL_30S',
    'Answer Rate': 'KPI_ANSWER_RATE',
    'Abandon Rate': 'KPI_ABANDON_RATE',
    'Average Talking Time': 'KPI_AVG_TALKING_TIME',
    'Maximum Talking Time': 'KPI_MAX_TALKING_TIME',
    'Average Time of Ringing': 'KPI_AVG_RINGING_TIME',
    'Average Queue Time': 'KPI_AVG_QUEUE_TIME',
    'Maximum Queue Time': 'KPI_MAX_QUEUE_TIME'
};

const TableWidget = ({ data, config }) => {
    const { t } = useTranslation();

    const translateKpiLabel = (value) => {
        const key = KPI_LABEL_TO_I18N[value];
        return key ? t(key) : value;
    };

    // Format value based on column format
    const formatValue = (value, format) => {
        if (value === null || value === undefined) return '-';

        switch (format) {
            case 'duration':
                // Convert seconds to mm:ss format
                const minutes = Math.floor(value / 60);
                const seconds = Math.floor(value % 60);
                return `${minutes}:${seconds.toString().padStart(2, '0')}`;

            case 'percentage':
                return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}%`;

            case 'number':
                return value.toLocaleString();

            default:
                return value;
        }
    };

    // Vérifier que data est un tableau valide
    if (!Array.isArray(data) || data.length === 0) {
        return null; // Le composant parent Widget affichera l'état vide
    }

    // Auto-generate columns from data keys if not provided
    const configColumns = config?.config?.columns || config?.columns;
    let columns;

    if (configColumns) {
        columns = configColumns;
    } else {
        // Generate columns dynamically from first data row
        const firstRow = data[0];

        // Detect the label column: 'kpi', 'range', or a known semantic key,
        // otherwise fall back to the first key in the row.
        const KNOWN_LABEL_KEYS = ['kpi', 'range', 'fromQueue', 'from', 'path'];
        const LABEL_HEADER_KEYS = {
            kpi: 'KPI',
            range: 'TIME_RANGE',
            fromQueue: 'FROM_QUEUE',
            from: 'FROM_QUEUE',
            path: 'QUEUE_PATH'
        };
        const labelKey =
            KNOWN_LABEL_KEYS.find(k => firstRow.hasOwnProperty(k)) ||
            Object.keys(firstRow)[0];

        const headerI18nKey = LABEL_HEADER_KEYS[labelKey];
        const labelColumn = {
            key: labelKey,
            name: headerI18nKey ? t(headerI18nKey) : labelKey,
            width: 200
        };

        // Get all keys except metadata keys AND the label key itself.
        const metadataKeys = ['kpi', 'range', 'format', 'agentName', 'flowName', 'queueName', 'agentId', 'flow', 'queue'];
        const dataKeys = Object.keys(firstRow).filter(
            key => !metadataKeys.includes(key) && key !== labelKey
        );

        columns = [
            labelColumn,
            ...dataKeys.map(key => ({
                key: key,
                name: key,
                width: 120
            }))
        ];
    }

    return (
        <div className="table-widget">
            <div className="analytics-table-container">
                <table>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key} style={{ width: col.width }}>
                                    {col.nameKey ? t(col.nameKey) : col.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => {
                            // Get row-level format if it exists
                            const rowFormat = row.format;

                            return (
                                <tr key={idx}>
                                    {columns.map(col => {
                                        // Never apply row-level format to label columns (kpi, range) - they're text, not metrics
                                        const isLabelColumn = col.key === 'kpi' || col.key === 'range';
                                        const cellFormat = isLabelColumn ? undefined : (col.format || rowFormat);

                                        return (
                                            <td key={col.key}>
                                                {col.formatter
                                                    ? col.formatter(row[col.key])
                                                    : col.key === 'kpi'
                                                        ? translateKpiLabel(row[col.key])
                                                        : formatValue(row[col.key], cellFormat)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TableWidget;
